import { useState, useMemo, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Search, RotateCw } from 'lucide-react';
import { apiGet, apiPost, apiPatch, apiDelete, ApiError } from '@/shared/api/client';
import { useAuth } from '@/shared/auth/AuthProvider';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { EntitySelect } from '@/components/ui/EntitySelect';
import { ImageUploadField } from '@/components/ui/ImageUploadField';
import type { Role } from '@/shared/auth/AuthProvider';

export type FieldType = 'text' | 'number' | 'email' | 'select' | 'checkbox' | 'textarea' | 'date' | 'entity' | 'image';

export interface FieldDef<T = any> {
  name: string;
  label: string;
  type: FieldType;
  required?: boolean;
  hint?: string;
  options?: Array<{ value: string; label: string }>;
  defaultValue?: any;
  hideOnEdit?: boolean;
  hideOnCreate?: boolean;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  parse?: (v: any) => any;
  format?: (v: any) => any;
  visible?: (form: T) => boolean;
  endpoint?: string;
  displayField?: string;
  secondaryField?: string;
  filter?: (row: any) => boolean;
  disabledValue?: string;
  uploadEntity?: 'equipment' | 'user' | 'item_model' | 'kit_template' | 'item' | 'kit' | 'maintenance' | 'recall' | 'bio_indicator' | 'batch';
  uploadKind?: 'PHOTO' | 'EVIDENCE' | 'MANUAL' | 'REPORT' | 'BIOMETRIC';
}

export interface ColumnDef<T = any> {
  key: string;
  label: string;
  render?: (row: T) => ReactNode;
  className?: string;
}

export interface EntityTableProps<T = any> {
  title: string;
  subtitle?: string;
  endpoint: string;
  queryKey: string;
  columns: ColumnDef<T>[];
  fields: FieldDef<T>[];
  writeRoles?: Role[];
  deleteRoles?: Role[];
  itemLabel?: (row: T) => string;
  canDelete?: boolean;
  beforeSubmit?: (form: any) => any;
}

function emptyForm(fields: FieldDef[], row?: any) {
  const f: Record<string, any> = {};
  for (const fd of fields) {
    if (row && row[fd.name] !== undefined && row[fd.name] !== null) f[fd.name] = row[fd.name];
    else if (fd.defaultValue !== undefined) f[fd.name] = fd.defaultValue;
    else f[fd.name] = fd.type === 'checkbox' ? false : '';
  }
  return f;
}

function stripEmpty(form: Record<string, any>, fields: FieldDef[]) {
  const out: Record<string, any> = {};
  for (const fd of fields) {
    const v = form[fd.name];
    if (v === '' || v === null || v === undefined) {
      if (fd.required) out[fd.name] = v;
      continue;
    }
    if (fd.type === 'number' && typeof v === 'string') out[fd.name] = Number(v);
    else out[fd.name] = v;
    if (fd.parse) out[fd.name] = fd.parse(out[fd.name]);
  }
  return out;
}

export function EntityTable<T extends { id: string; active?: number }>(props: EntityTableProps<T>) {
  const {
    title, subtitle, endpoint, queryKey, columns, fields,
    writeRoles = ['ADMIN', 'SUPERVISOR'], deleteRoles = ['ADMIN'],
    itemLabel = (r: any) => r.name ?? r.code ?? r.id, canDelete = true, beforeSubmit,
  } = props;

  const { hasRole } = useAuth();
  const canWrite = hasRole(...writeRoles);
  const canDel = canDelete && hasRole(...deleteRoles);
  const qc = useQueryClient();

  const [q, setQ] = useState('');
  const [editing, setEditing] = useState<T | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<T | null>(null);
  const [form, setForm] = useState<Record<string, any>>({});
  const [error, setError] = useState<string | null>(null);

  const list = useQuery({
    queryKey: [queryKey, q],
    queryFn: () => apiGet<{ items: T[] }>(`${endpoint}?q=${encodeURIComponent(q)}&limit=200`),
    refetchInterval: 60_000,
  });

  const createM = useMutation({
    mutationFn: (body: any) => apiPost(endpoint, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [queryKey] }); setCreating(false); setError(null); },
    onError: (e) => setError(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e)),
  });
  const updateM = useMutation({
    mutationFn: ({ id, body }: { id: string; body: any }) => apiPatch(`${endpoint}/${id}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [queryKey] }); setEditing(null); setError(null); },
    onError: (e) => setError(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e)),
  });
  const deleteM = useMutation({
    mutationFn: (id: string) => apiDelete(`${endpoint}/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [queryKey] }); setConfirming(null); },
  });

  const visibleFields = useMemo(
    () => fields.filter((f) => (editing ? !f.hideOnEdit : !f.hideOnCreate) && (!f.visible || f.visible(form as any))),
    [fields, editing, form],
  );

  function openCreate() {
    setForm(emptyForm(fields));
    setError(null);
    setCreating(true);
  }
  function openEdit(row: T) {
    setForm(emptyForm(fields, row));
    setError(null);
    setEditing(row);
  }
  function submit() {
    let body = stripEmpty(form, fields);
    if (beforeSubmit) body = beforeSubmit(body);
    if (editing) updateM.mutate({ id: editing.id, body });
    else createM.mutate(body);
  }

  const rows = list.data?.items ?? [];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title={title}
          subtitle={subtitle}
          action={
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" leadingIcon={<RotateCw className="h-4 w-4" />} onClick={() => list.refetch()}>
                Atualizar
              </Button>
              {canWrite ? (
                <Button size="sm" leadingIcon={<Plus className="h-4 w-4" />} onClick={openCreate}>
                  Novo
                </Button>
              ) : null}
            </div>
          }
        />
        <CardBody>
          <div className="mb-3 max-w-sm">
            <Input
              placeholder="Buscar..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              leadingIcon={<Search className="h-4 w-4" />}
            />
          </div>
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-neutral-200 text-left text-xs uppercase tracking-wide text-neutral-500 dark:border-neutral-700">
                  {columns.map((c) => (
                    <th key={c.key} className={`px-3 py-2 font-medium ${c.className ?? ''}`}>{c.label}</th>
                  ))}
                  <th className="px-3 py-2 text-right font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {list.isLoading ? (
                  <tr><td colSpan={columns.length + 1} className="px-3 py-6 text-center text-neutral-500">Carregando...</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={columns.length + 1} className="px-3 py-6 text-center text-neutral-500">Nenhum registro.</td></tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.id} className="border-b border-neutral-100 hover:bg-neutral-50 dark:border-neutral-700/50 dark:hover:bg-neutral-700/30">
                      {columns.map((c) => (
                        <td key={c.key} className={`px-3 py-2 ${c.className ?? ''}`}>
                          {c.render ? c.render(r) : (r as any)[c.key] ?? '—'}
                        </td>
                      ))}
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex items-center gap-1">
                          {r.active === 0 ? <Badge tone="neutral">Inativo</Badge> : null}
                          {canWrite ? (
                            <button
                              type="button"
                              onClick={() => openEdit(r)}
                              className="rounded p-1.5 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                              aria-label="Editar"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                          ) : null}
                          {canDel && r.active !== 0 ? (
                            <button
                              type="button"
                              onClick={() => setConfirming(r)}
                              className="rounded p-1.5 text-danger hover:bg-danger-bg"
                              aria-label="Desativar"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      <Modal
        open={creating || !!editing}
        title={editing ? `Editar ${itemLabel(editing)}` : `Novo registro`}
        onClose={() => { setCreating(false); setEditing(null); setError(null); }}
        footer={
          <>
            <Button variant="ghost" onClick={() => { setCreating(false); setEditing(null); setError(null); }}>Cancelar</Button>
            <Button onClick={submit} loading={createM.isPending || updateM.isPending}>{editing ? 'Salvar' : 'Criar'}</Button>
          </>
        }
      >
        <form className="grid grid-cols-1 gap-3 sm:grid-cols-2" onSubmit={(e) => { e.preventDefault(); submit(); }}>
          {visibleFields.map((f) => (
            <FieldRenderer
              key={f.name}
              field={f}
              value={form[f.name]}
              entityId={editing?.id}
              onChange={(v) => setForm((s) => ({ ...s, [f.name]: v }))}
            />
          ))}
        </form>
        {error ? <p className="mt-3 text-sm text-danger">{error}</p> : null}
      </Modal>

      <Modal
        open={!!confirming}
        title="Desativar registro"
        size="sm"
        onClose={() => setConfirming(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirming(null)}>Cancelar</Button>
            <Button variant="danger" onClick={() => confirming && deleteM.mutate(confirming.id)} loading={deleteM.isPending}>
              Desativar
            </Button>
          </>
        }
      >
        <p className="text-sm text-neutral-700 dark:text-neutral-300">
          Confirmar desativação de <strong>{confirming ? itemLabel(confirming) : ''}</strong>?
          O registro permanece para auditoria, mas não aparecerá em listas operacionais.
        </p>
      </Modal>
    </div>
  );
}

function FieldRenderer({
  field, value, entityId, onChange,
}: { field: FieldDef; value: any; entityId?: string; onChange: (v: any) => void }) {
  const span = field.type === 'textarea' ? 'sm:col-span-2' : '';

  if (field.type === 'checkbox') {
    return (
      <label className={`flex items-center gap-2 ${span}`}>
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          className="h-4 w-4 rounded border-neutral-300 text-brand-500 focus:ring-brand-500"
        />
        <span className="text-sm text-neutral-700 dark:text-neutral-300">{field.label}</span>
      </label>
    );
  }
  if (field.type === 'entity') {
    return (
      <div className={span}>
        <EntitySelect
          label={field.label}
          required={field.required}
          hint={field.hint}
          placeholder={field.placeholder}
          value={value ?? ''}
          onChange={onChange}
          endpoint={field.endpoint!}
          displayField={field.displayField}
          secondaryField={field.secondaryField}
          filter={field.filter}
          disabledValue={field.disabledValue}
        />
      </div>
    );
  }
  if (field.type === 'image') {
    return (
      <ImageUploadField
        label={field.label}
        required={field.required}
        hint={field.hint}
        value={value ?? ''}
        onChange={onChange}
        entity={field.uploadEntity ?? 'item_model'}
        entityId={entityId}
        kind={field.uploadKind ?? 'PHOTO'}
      />
    );
  }
  if (field.type === 'select') {
    return (
      <div className={`flex flex-col gap-1.5 ${span}`}>
        <label className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
          {field.label}{field.required ? ' *' : ''}
        </label>
        <select
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 rounded border border-neutral-300 bg-neutral-0 px-3 text-sm outline-none focus:border-brand-500 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
        >
          <option value="">{field.placeholder ?? '— Selecione —'}</option>
          {field.options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {field.hint ? <p className="text-xs text-neutral-500">{field.hint}</p> : null}
      </div>
    );
  }
  if (field.type === 'textarea') {
    return (
      <div className={`flex flex-col gap-1.5 ${span}`}>
        <label className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
          {field.label}{field.required ? ' *' : ''}
        </label>
        <textarea
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="rounded border border-neutral-300 bg-neutral-0 p-2 text-sm outline-none focus:border-brand-500 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
        />
        {field.hint ? <p className="text-xs text-neutral-500">{field.hint}</p> : null}
      </div>
    );
  }
  return (
    <Input
      label={field.label + (field.required ? ' *' : '')}
      type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : field.type === 'email' ? 'email' : 'text'}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      hint={field.hint}
      placeholder={field.placeholder}
      min={field.min}
      max={field.max}
      step={field.step}
    />
  );
}
